import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// Confirms a decision for several proposals at once and then works through them one by one.
//
// Deliberately sequential rather than one bulk request: every proposal keeps its own approval,
// its own audit entry and its own apply result, exactly as if it had been decided by hand — a
// batch that fails halfway leaves no half-applied combined request behind, and the dialog can
// name the proposals that did not go through.
function AiProposalBulkDecisionDialog({ open, mode, proposals = [], onCancel, onFinished, runOne }) {
  const noteInputRef = useRef(null);
  const [noteError, setNoteError] = useState(false);
  const [running, setRunning] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [results, setResults] = useState(null);
  const [abortNote, setAbortNote] = useState(null);

  const isReject = mode === 'reject';
  const total = proposals.length;

  // A fresh dialog every time it opens — an old run's results must never be mistaken for this one.
  useEffect(() => {
    if (!open) return;
    setNoteError(false);
    setRunning(false);
    setDoneCount(0);
    setResults(null);
    setAbortNote(null);
    if (noteInputRef.current) noteInputRef.current.value = '';
  }, [open]);

  const handleConfirm = async () => {
    const note = noteInputRef.current?.value?.trim() || '';
    if (isReject && !note) {
      setNoteError(true);
      return;
    }
    setNoteError(false);
    setRunning(true);

    const collected = [];
    const decidedIds = [];

    for (const proposal of proposals) {
      const id = proposal?._id;
      if (!id) continue;
      try {
        const response = await runOne(id, note);
        // The hook returns null when the request was stored for step-up authentication. Every
        // further call would queue another approval request, so stop instead of flooding it.
        if (response === null) {
          setAbortNote(
            'Die Entscheidung wurde zur Freigabe eingereicht. Die übrigen ausgewählten ' +
              'Vorschläge wurden nicht bearbeitet.'
          );
          break;
        }
        decidedIds.push(id);
        const updated = response?.aiProposal;
        const applyFailed = mode === 'approve' && updated?.status === 'failed';
        collected.push({
          id,
          summary: proposal.summary || id,
          ok: !applyFailed,
          error: applyFailed
            ? updated?.applyResult?.error || 'Anwendung fehlgeschlagen'
            : undefined,
        });
      } catch (err) {
        collected.push({
          id,
          summary: proposal.summary || id,
          ok: false,
          error: err?.message || 'Unbekannter Fehler',
        });
      } finally {
        setDoneCount(c => c + 1);
      }
    }

    setResults(collected);
    setRunning(false);
    if (typeof onFinished === 'function') onFinished(decidedIds);
  };

  const failed = (results || []).filter(r => !r.ok);
  const succeeded = (results || []).length - failed.length;
  const finished = results !== null;

  return (
    <Dialog
      open={open}
      onClose={running ? undefined : onCancel}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>
        {isReject
          ? `${total} ${total === 1 ? 'Vorschlag' : 'Vorschläge'} ablehnen`
          : `${total} ${total === 1 ? 'Vorschlag' : 'Vorschläge'} annehmen`}
      </DialogTitle>
      <DialogContent>
        {finished ? (
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant='body2'>
              {isReject ? `${succeeded} abgelehnt` : `${succeeded} angenommen und angewendet`}
              {failed.length > 0 ? `, ${failed.length} fehlgeschlagen` : ''}.
            </Typography>
            {abortNote ? (
              <Typography variant='body2' color='text.secondary'>
                {abortNote}
              </Typography>
            ) : null}
            {failed.length > 0 ? (
              <Box>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                  Nicht durchgelaufen:
                </Typography>
                <Stack spacing={0.5}>
                  {failed.map(r => (
                    <Typography key={r.id} variant='body2' color='error'>
                      {r.summary}
                      {r.error ? ` — ${r.error}` : ''}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        ) : running ? (
          <Stack spacing={1} sx={{ pt: 1 }}>
            <LinearProgress
              variant='determinate'
              value={total > 0 ? (doneCount / total) * 100 : 0}
            />
            <Typography variant='body2' color='text.secondary'>
              {doneCount} von {total} bearbeitet …
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              {isReject
                ? 'Der Grund gilt für alle ausgewählten Vorschläge.'
                : 'Jeder ausgewählte Vorschlag wird einzeln angewendet. Die Notiz gilt für alle.'}
            </Typography>
            <TextField
              inputRef={noteInputRef}
              label={isReject ? 'Ablehnungsgrund' : 'Notiz (optional)'}
              required={isReject}
              error={noteError}
              helperText={noteError ? 'Bitte gib einen Ablehnungsgrund an' : ' '}
              multiline
              minRows={3}
              fullWidth
            />
            <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
              <Stack spacing={0.5}>
                {proposals.map(p => (
                  <Typography key={p._id} variant='body2' color='text.secondary'>
                    {p.summary || p._id}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {finished ? (
          <Button variant='contained' onClick={onCancel}>
            Schließen
          </Button>
        ) : (
          <>
            <Button onClick={onCancel} disabled={running}>
              Abbrechen
            </Button>
            <Button
              variant='contained'
              color={isReject ? 'error' : 'primary'}
              onClick={handleConfirm}
              disabled={running || total === 0}
              startIcon={running ? <CircularProgress size={16} color='inherit' /> : null}
            >
              {isReject ? 'Alle ablehnen' : 'Alle annehmen'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AiProposalBulkDecisionDialog;
